import React, { useEffect, useId, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FieldErrors, FieldValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { getCustomerContacts, OrderContact } from '../../../services/requests/orders/orders-requests';
import { RequiredLabel } from '../../UI/view/FormItems/RequiredLabel';

interface OrderContactFieldProps {
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors?: FieldErrors;
  required?: boolean;
  writeAccess?: boolean;
}

export const OrderContactField = ({ register, watch, setValue, errors, required, writeAccess = true }: OrderContactFieldProps) => {
  const listId = useId();
  const customerId = Number(watch('customerId'));
  const orderTypeId = Number(watch('orderTypeId'));
  const name = String(watch('orderedBy') ?? '');
  const scope = `${orderTypeId}:${customerId}`;
  const [history, setHistory] = useState<{ scope: string; contacts: OrderContact[] }>();
  const [loadError, setLoadError] = useState('');
  const userChangedName = useRef(false);

  useEffect(() => {
    let active = true;
    userChangedName.current = false;
    setLoadError('');
    setHistory(undefined);
    if (customerId > 0 && orderTypeId > 0) {
      getCustomerContacts(orderTypeId, customerId).then(contacts => {
        if (active) setHistory({ scope, contacts });
      }).catch(() => {
        if (active) setLoadError('Unable to load previous order contacts. Enter contact details manually.');
      });
    }
    return () => { active = false; };
  }, [customerId, orderTypeId, scope]);

  const contacts = history?.scope === scope ? history.contacts : undefined;
  useEffect(() => {
    if (!userChangedName.current || !writeAccess) return;
    const contact = contacts?.find(c => c.name.trim().toLowerCase() === name.trim().toLowerCase());
    if (!contact) return;
    userChangedName.current = false;
    const options = { shouldDirty: true, shouldValidate: true };
    setValue('orderedByPhone', contact.phone, options);
    setValue('orderedByEmail', contact.email, options);
    setValue('orderedByFax', contact.fax, options);
  }, [contacts, name, setValue, writeAccess]);

  const error = errors?.orderedBy;
  return (
    <Form.Group className="mb-1" controlId={listId}>
      <RequiredLabel label="Ordered By Contact" required={required} />
      <Form.Control
        size="sm"
        list={`${listId}-contacts`}
        autoComplete="off"
        maxLength={100}
        required={required}
        disabled={!writeAccess}
        isInvalid={!!error}
        {...register('orderedBy', {
          required: required && 'Required',
          maxLength: { value: 100, message: 'Must be at most 100 characters' },
          onChange: () => { userChangedName.current = true; },
        })}
      />
      <datalist id={`${listId}-contacts`}>
        {contacts?.map(contact => <option key={contact.name} value={contact.name} />)}
      </datalist>
      {error && <Form.Control.Feedback type="invalid">{String(error.message ?? 'Invalid contact name')}</Form.Control.Feedback>}
      {loadError && <div role="alert" className="text-danger small">{loadError}</div>}
    </Form.Group>
  );
};
